const toText = (v) => (v ?? '-');
const getCommercialName = (carrier) => carrier?.xipp_commercial_name ?? '-';
const getLegalName = (owner) => owner?.legal_name ?? '-';
const getBenefitName = (benefit) =>
  (typeof benefit === 'object' && benefit && benefit.name)
  ? benefit.name
  : (benefit ?? '-');

export default class Functions {
  downloadFile(apiResponse) {
    const filename = apiResponse.headers['content-disposition'].split('"');
    const fileURL = window.URL.createObjectURL(new Blob([apiResponse.data]));
    const fileLink = document.createElement('a');
    const nameDecoded = decodeURIComponent(filename[1]);
    fileLink.href = fileURL;
    fileLink.setAttribute('download', nameDecoded);
    document.body.appendChild(fileLink);

    fileLink.click();
  }

  downloadBase64File(apiResponse) {
    const filename = apiResponse.headers['content-disposition'].split('"');
    const contentType = apiResponse.headers['content-type'];
    const fileLink = document.createElement('a');

    fileLink.href = `data:${contentType};base64,${apiResponse.data}`;
    const nameDecoded = decodeURIComponent(filename[1]);
    fileLink.setAttribute('download', nameDecoded);
    document.body.appendChild(fileLink);

    fileLink.click();
  }

  downloadURI = (uri) => {
    const link = document.createElement('a');
    link.href = uri;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  convertText(text) {
    return text && typeof text === 'string' && text.length > 0
      ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
      : '';
  }

  clearSessionStorage(keysToPreserve) {
    const preservedValues = {};

    keysToPreserve.forEach((key) => {
      const value = sessionStorage.getItem(key);
      if (value !== null) {
        preservedValues[key] = value;
      }
    });

    sessionStorage.clear();

    Object.entries(preservedValues).forEach(([key, value]) => {
      sessionStorage.setItem(key, value);
    });
  }

  formatContractDescription(type, contracts) {
    if (!Array.isArray(contracts) || contracts.length === 0) return [];

    return contracts.map((item) => {
      const { benefit_id, carrier_id, policy, contract_owner_id } = item;
      const name = benefit_id && benefit_id.name ? benefit_id.name : benefit_id || '-';
      const commercialName = carrier_id && carrier_id.xipp_commercial_name ? carrier_id.xipp_commercial_name : '-';
      const legalName = contract_owner_id && contract_owner_id.legal_name ? contract_owner_id.legal_name : '-';
      const text = type === 'byContractId' ? `${name} - ${commercialName} - ${policy || '-'} - ${legalName}` : `${name || '-'} - ${commercialName} - ${policy || '-'} - ${legalName}`;

      return {
        ...item,
        text,
      };
    })
    .sort((a, b) =>
      a.text.localeCompare(b.text, undefined, { sensitivity: 'base' })
    );
  }

  formatSubContractDescription(subContracts) {
    if (!Array.isArray(subContracts) || subContracts.length === 0) return [];

    const formatted = subContracts.map((item) => {
      const subcontractNumber = toText(item?.subcontract_number);
      const contractOwnerLegalName = getLegalName(item?.contract_owner_id);
      const text = `${subcontractNumber} - ${contractOwnerLegalName}`;
      return { ...item, text };
    });

    formatted.sort((a, b) => {
      const aSub = String(a && a.subcontract_number ? a.subcontract_number : '');
      const bSub = String(b && b.subcontract_number ? b.subcontract_number : '');
      const byNumber = aSub.localeCompare(bSub, undefined, { numeric: true, sensitivity: 'base' });
      if (byNumber !== 0) return byNumber;

      const alSub = getLegalName(a && a.contract_owner_id ? a.contract_owner_id : '');
      const blSub = getLegalName(b && b.contract_owner_id ? b.contract_owner_id : '');
      return alSub.localeCompare(blSub, undefined, { sensitivity: 'base' });
    });

    return formatted;
  }

  areEqualDeep(a, b) {
    if (a === b) return true;
    if (typeof a !== typeof b) {
      try {
        return JSON.stringify(a) === JSON.stringify(b);
      } catch(e) {
        return false;
      }
    }

    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((item, index) => this.areEqualDeep(item, b[index]));
    }

    if (a !== null && b !== null && typeof a === 'object' && typeof b === 'object') {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length) return false;

      return keysA.every(key => this.areEqualDeep(a[key], b[key]));
    }

    return a == b;
  }
}
